// src/hooks/useDataCache.ts
// Hook untuk caching data yang efisien menggunakan React Query pattern

import { useState, useEffect, useRef } from 'react';

// Cache storage interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

// Cache configuration
interface CacheConfig {
  staleTime?: number; // Time in ms before data is considered stale (default: 5 minutes)
  cacheTime?: number; // Time in ms before data is removed from cache (default: 30 minutes)
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
}

class DataCache {
  private cache = new Map<string, CacheEntry<any>>();
  private subscribers = new Map<string, Set<() => void>>();

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    // Check if data has expired
    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  set<T>(key: string, data: T, cacheTime: number = 30 * 60 * 1000): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + cacheTime
    };
    
    this.cache.set(key, entry);
    this.notifySubscribers(key);
  }

  isStale(key: string, staleTime: number = 5 * 60 * 1000): boolean {
    const entry = this.cache.get(key);
    if (!entry) return true;
    
    return Date.now() - entry.timestamp > staleTime;
  }

  subscribe(key: string, callback: () => void): () => void {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    
    this.subscribers.get(key)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      const keySubscribers = this.subscribers.get(key);
      if (keySubscribers) {
        keySubscribers.delete(callback);
        if (keySubscribers.size === 0) {
          this.subscribers.delete(key);
        }
      }
    };
  }

  private notifySubscribers(key: string): void {
    const keySubscribers = this.subscribers.get(key);
    if (keySubscribers) {
      keySubscribers.forEach(callback => callback());
    }
  }

  invalidate(key: string): void {
    this.cache.delete(key);
    this.notifySubscribers(key);
  }

  clear(): void {
    this.cache.clear();
    this.subscribers.clear();
  }
}

// Global cache instance
const globalCache = new DataCache();

// Hook for cached data fetching
export function useCachedData<T>(
  key: string,
  fetcher: () => Promise<T>,
  config: CacheConfig = {}
) {
  const {
    staleTime = 5 * 60 * 1000, // 5 minutes
    cacheTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = true
  } = config;

  const [data, setData] = useState<T | null>(() => globalCache.get<T>(key));
  const [loading, setLoading] = useState<boolean>(!data);
  const [error, setError] = useState<string | null>(null);
  const fetcherRef = useRef(fetcher);
  const isFetchingRef = useRef(false);

  // Update fetcher ref
  fetcherRef.current = fetcher;

  const fetchData = async (force = false) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current && !force) return;
    
    const cachedData = globalCache.get<T>(key);
    const isStale = globalCache.isStale(key, staleTime);
    
    // Return cached data if it exists and is not stale (unless forced)
    if (cachedData && !isStale && !force) {
      setData(cachedData);
      setLoading(false);
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      const result = await fetcherRef.current();
      
      globalCache.set(key, result, cacheTime);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error(`Error fetching data for key "${key}":`, err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const refetch = () => fetchData(true);

  const invalidate = () => {
    globalCache.invalidate(key);
    setData(null);
  };

  // Subscribe to cache updates
  useEffect(() => {
    const unsubscribe = globalCache.subscribe(key, () => {
      const newData = globalCache.get<T>(key);
      setData(newData);
    });

    return unsubscribe;
  }, [key]);

  // Initial fetch
  useEffect(() => {
    if (refetchOnMount || !data) {
      fetchData();
    }
  }, [key]); // Only depend on key

  // Refetch on window focus
  useEffect(() => {
    if (!refetchOnWindowFocus) return;

    const handleFocus = () => {
      if (globalCache.isStale(key, staleTime)) {
        fetchData();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [key, staleTime, refetchOnWindowFocus]);

  return {
    data,
    loading,
    error,
    refetch,
    invalidate,
    isStale: data ? globalCache.isStale(key, staleTime) : true
  };
}

// Hook for batch data fetching
export function useBatchedData(
  queries: Array<{
    key: string;
    fetcher: () => Promise<any>;
    config?: CacheConfig;
  }>
) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const results = queries.map(query => 
    useCachedData(query.key, query.fetcher, query.config)
  );

  const data = results.reduce((acc, result, index) => {
    const key = queries[index].key;
    (acc as any)[key] = result.data;
    return acc;
  }, {} as Record<string, any>);

  const loading = results.some(result => result.loading);
  const error = results.find(result => result.error)?.error || null;

  const refetchAll = () => {
    results.forEach(result => result.refetch());
  };

  const invalidateAll = () => {
    results.forEach(result => result.invalidate());
  };

  return {
    data,
    loading,
    error,
    refetchAll,
    invalidateAll,
    results
  };
}

// Export cache instance for manual operations
export { globalCache };

// Utility function to prefetch data
export const prefetchData = async <T>(
  key: string,
  fetcher: () => Promise<T>,
  cacheTime?: number
) => {
  try {
    const data = await fetcher();
    globalCache.set(key, data, cacheTime);
    return data;
  } catch (error) {
    console.error(`Error prefetching data for key "${key}":`, error);
    throw error;
  }
};